import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import PictureAsPdfOutlined from '@mui/icons-material/PictureAsPdfOutlined';
import VisibilityOffOutlined from '@mui/icons-material/VisibilityOffOutlined';
import FileDownloadOutlined from '@mui/icons-material/FileDownloadOutlined';
import CloseOutlined from '@mui/icons-material/CloseOutlined';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import ArrowBack from '@mui/icons-material/ArrowBack';
import * as S from 'pages/Navigation/components/ManagerReportViewer.styled';

type OverlayMobileSection = 'report' | 'pdf';

export type CenteredPreviewPdfActions = {
  viewUrl: string
  isPdfLoading: boolean
  onOpenPdf: () => void
  onDownloadPdf: () => void
  reportTabLabel?: string
};

export type CenteredPreviewOverlayProps = {
  subject: string
  children: React.ReactNode
  onClose?: () => void
  previewKind?: string
  draftSubtitle?: string
  ariaLabel?: string
  pdf?: CenteredPreviewPdfActions | null
  /** Full-page public form — same chrome, no close. */
  asPage?: boolean
  /** Work-order /form preview — light orange-dark chrome. */
  workOrderPreview?: boolean
  /** Stretch body to full width inside preview margins (asset renting board). */
  wideContent?: boolean
  /** Override default 520px form card width (e.g. assets mobility +100 → 620). */
  cardMaxWidthPx?: number
  /** Shown when the form can return to a previous page (history or stored return path). */
  onBack?: () => void
};

const CenteredPreviewOverlay = ({
  subject,
  children,
  onClose,
  previewKind = 'Preview',
  draftSubtitle,
  ariaLabel,
  pdf = null,
  asPage = false,
  workOrderPreview = false,
  wideContent = false,
  cardMaxWidthPx,
  onBack,
}: CenteredPreviewOverlayProps) => {
  const [showPdf, setShowPdf] = useState(false);
  const [mobileSection, setMobileSection] = useState<OverlayMobileSection>('report');
  const [isClosing, setIsClosing] = useState(false);
  const hasPdf = Boolean(pdf);
  const canClose = Boolean(onClose) && !asPage;
  // Any full-page form may offer Back when the caller has a previous page.
  const canBack = Boolean(onBack) && asPage;
  const resolvedAria = ariaLabel || `${previewKind} preview`;
  const resolvedCardMaxWidth = Number.isFinite(Number(cardMaxWidthPx)) && Number(cardMaxWidthPx) > 0
    ? Math.floor(Number(cardMaxWidthPx))
    : 520;
  const onTogglePdf = () => {
    if (!pdf) return;
    if (showPdf) {
      setShowPdf(false);
      return;
    }
    pdf.onOpenPdf();
    setShowPdf(true);
  };

  const requestClose = () => {
    if (!canClose || isClosing) return;
    setIsClosing(true);
  };

  useEffect(() => {
    if (!isClosing) return;
    if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    onClose?.();
  }, [isClosing, onClose]);

  const overlay = (
    <S.Overlay
      role="dialog"
      aria-modal
      aria-label={resolvedAria}
      $exiting={isClosing}
      onAnimationEnd={(e) => {
        if (e.target !== e.currentTarget) return;
        if (isClosing) onClose?.();
      }}
    >
      <S.TopBar>
        <S.TitleBlock>
          <S.Title>
            <S.KindPill>{previewKind}</S.KindPill>
            <S.TitleText>{subject || previewKind}</S.TitleText>
          </S.Title>
          {draftSubtitle ? <S.Sub>{draftSubtitle}</S.Sub> : null}
        </S.TitleBlock>
        <S.Actions>
          {hasPdf && pdf ? (
            <>
              <S.DesktopOnly>
                <S.Btn
                  type="button"
                  onClick={onTogglePdf}
                  disabled={pdf.isPdfLoading && !pdf.viewUrl}
                >
                  {showPdf ? <VisibilityOffOutlined /> : <PictureAsPdfOutlined />}
                  {showPdf ? 'Hide PDF' : 'View PDF'}
                </S.Btn>
              </S.DesktopOnly>
              <S.Btn
                type="button"
                onClick={pdf.onDownloadPdf}
                disabled={pdf.isPdfLoading && !pdf.viewUrl}
              >
                <FileDownloadOutlined />
                {pdf.isPdfLoading && !pdf.viewUrl ? 'Preparing PDF…' : 'Download PDF'}
              </S.Btn>
            </>
          ) : null}
          {canBack ? (
            <S.Btn type="button" onClick={onBack}>
              <ArrowBack />
              Back
            </S.Btn>
          ) : null}
          {canClose ? (
            <S.Btn type="button" onClick={requestClose}>
              <CloseOutlined />
              Close
            </S.Btn>
          ) : null}
        </S.Actions>
      </S.TopBar>

      {hasPdf && pdf ? (
        <S.MobileSectionTabs>
          <S.MobileSectionTab
            type="button"
            $active={mobileSection === 'report'}
            onClick={() => setMobileSection('report')}
          >
            <DescriptionOutlined />
            {pdf.reportTabLabel || 'Report'}
          </S.MobileSectionTab>
          <S.MobileSectionTab
            type="button"
            $active={mobileSection === 'pdf'}
            onClick={() => {
              pdf.onOpenPdf();
              setMobileSection('pdf');
            }}
          >
            <PictureAsPdfOutlined />
            PDF
          </S.MobileSectionTab>
        </S.MobileSectionTabs>
      ) : null}

      {hasPdf && pdf ? (
        <S.FrameWrap
          $hideOnDesktop={!showPdf}
          $hideOnMobile={mobileSection !== 'pdf'}
        >
          <S.PdfStage>
            {pdf.isPdfLoading && !pdf.viewUrl ? (
              <S.Empty>Opening report…</S.Empty>
            ) : pdf.viewUrl ? (
              <S.Frame title={resolvedAria} src={pdf.viewUrl} />
            ) : (
              <S.Empty>PDF could not be loaded.</S.Empty>
            )}
          </S.PdfStage>
        </S.FrameWrap>
      ) : null}

      <S.PreviewWrap
        $centerCard={!wideContent}
        $wideContent={wideContent}
        $hideOnDesktop={hasPdf && showPdf}
        $hideOnMobile={hasPdf && mobileSection !== 'report'}
      >
        <S.PreviewCard
          $centered={!wideContent}
          $wideContent={wideContent}
          $workOrderPreview={workOrderPreview}
          $maxWidthPx={wideContent ? undefined : resolvedCardMaxWidth}
        >
          {children}
        </S.PreviewCard>
      </S.PreviewWrap>
    </S.Overlay>
  );

  return createPortal(overlay, document.body);
};

export default CenteredPreviewOverlay;
